import React from "react";
import { Button, Card, CardBody, CardHeader, Col, Label, Row } from "reactstrap";
import Flatpickr from "react-flatpickr";
import Select from "src/components/Common/Select";
import type { QuickRangeKey, SelectOption } from "./types";

interface FiltersPanelProps {
  filtersOpen: boolean;
  rangeLabel: string;
  dateRange: [Date | null, Date | null];
  onToggle: () => void;
  onDateChange: (selectedDates: Date[]) => void;
  onQuickRange: (key: QuickRangeKey) => void;
  selectedQuickRange: QuickRangeKey | null;
  officeOptions: SelectOption[];
  teamOptions: SelectOption[];
  brokerOptions: SelectOption[];
  countryOptions: SelectOption[];
  payMethodOptions: SelectOption[];
  statusOptions: SelectOption[];
  userOptions: SelectOption[];
  selectedOffices: SelectOption[];
  selectedTeams: SelectOption[];
  selectedBrokers: SelectOption[];
  selectedCountries: SelectOption[];
  selectedPayMethods: SelectOption[];
  selectedStatuses: SelectOption[];
  selectedResponsibles: SelectOption[];
  onOfficesChange: (value: SelectOption[]) => void;
  onTeamsChange: (value: SelectOption[]) => void;
  onBrokersChange: (value: SelectOption[]) => void;
  onCountriesChange: (value: SelectOption[]) => void;
  onPayMethodsChange: (value: SelectOption[]) => void;
  onStatusesChange: (value: SelectOption[]) => void;
  onResponsiblesChange: (value: SelectOption[]) => void;
  onReset: () => void;
  hasFilters: boolean;
}

const FiltersPanel: React.FC<FiltersPanelProps> = ({
  filtersOpen,
  rangeLabel,
  dateRange,
  onToggle,
  onDateChange,
  onQuickRange,
  selectedQuickRange,
  officeOptions,
  teamOptions,
  brokerOptions,
  countryOptions,
  payMethodOptions,
  statusOptions,
  userOptions,
  selectedOffices,
  selectedTeams,
  selectedBrokers,
  selectedCountries,
  selectedPayMethods,
  selectedStatuses,
  selectedResponsibles,
  onOfficesChange,
  onTeamsChange,
  onBrokersChange,
  onCountriesChange,
  onPayMethodsChange,
  onStatusesChange,
  onResponsiblesChange,
  onReset,
  hasFilters,
}) => {
  const flatpickrValue = dateRange.filter(Boolean) as Date[];
  const quickRanges: Array<{ key: QuickRangeKey; label: string }> = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "this_week", label: "This week" },
    { key: "last_week", label: "Last week" },
    { key: "this_month", label: "This month" },
    { key: "last_month", label: "Last month" },
    { key: "this_year", label: "This year" },
    { key: "last_year", label: "Last year" },
  ];

  return (
    <Row className="align-items-center">
      <Col md={12}>
        <Card className="card-h-100">
          <CardHeader>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h4 className="card-title mb-1">Filters</h4>
                <div className="text-muted small">{rangeLabel}</div>
              </div>
              <Button
                color="link"
                className="p-0 text-decoration-none"
                onClick={onToggle}
              >
                {filtersOpen ? "Hide filters" : "Show filters"}
              </Button>
            </div>
          </CardHeader>
          {filtersOpen && (
              <CardBody>
                  <Flatpickr
                      className="form-control d-block"
                      placeholder="dd M,yyyy"
                      options={{
                          mode: "range",
                          dateFormat: "Y-m-d",
                      }}
                      value={flatpickrValue}
                      onChange={onDateChange}
                  />
                  <div className="d-flex flex-wrap gap-2 mt-2">
                      {quickRanges.map((range) => (
                          <Button
                              key={range.key}
                              type="button"
                              color={range.key === selectedQuickRange ? "primary" : "light"}
                              outline={range.key !== selectedQuickRange}
                              size="sm"
                              onClick={() => onQuickRange(range.key)}
                          >
                              {range.label}
                          </Button>
                      ))}
                  </div>
                  <Row className="g-3 mt-2">
                      <Col xl={3} md={6}>
                          <Label className="form-label">Offices</Label>
                          <Select
                              isMulti
                              isClearable
                              placeholder="All offices"
                              options={officeOptions}
                              value={selectedOffices}
                              onChange={(value: any) => onOfficesChange(value || [])}
                          />
                      </Col>
                      <Col xl={3} md={6}>
                          <Label className="form-label">Buyer teams</Label>
                          <Select
                              isMulti
                              isClearable
                              placeholder="All teams"
                              options={teamOptions}
                              value={selectedTeams}
                              onChange={(value: any) => onTeamsChange(value || [])}
                              isDisabled={!teamOptions.length}
                          />
                      </Col>
                      <Col xl={3} md={6}>
                          <Label className="form-label">Brokers</Label>
                          <Select
                              isMulti
                              isClearable
                              placeholder="All brokers"
                              options={brokerOptions}
                              value={selectedBrokers}
                              onChange={(value: any) => onBrokersChange(value || [])}
                              isDisabled={!brokerOptions.length}
                          />
                      </Col>
                      <Col xl={3} md={6}>
                          <Label className="form-label">Countries</Label>
                          <Select
                              isMulti
                              isClearable
                              placeholder="All countries"
                              options={countryOptions}
                              value={selectedCountries}
                              onChange={(value: any) => onCountriesChange(value || [])}
                              isDisabled={!countryOptions.length}
                          />
                      </Col>
                      <Col xl={3} md={6}>
                          <Label className="form-label">Pay model</Label>
                          <Select
                              isMulti
                              isClearable
                              placeholder="All models"
                              options={payMethodOptions}
                              value={selectedPayMethods}
                              onChange={(value: any) => onPayMethodsChange(value || [])}
                          />
                      </Col>
                      <Col xl={3} md={6}>
                          <Label className="form-label">Statuses</Label>
                          <Select
                              isMulti
                              isClearable
                              placeholder="All statuses"
                              options={statusOptions}
                              value={selectedStatuses}
                              onChange={(value: any) => onStatusesChange(value || [])}
                          />
                      </Col>
                      <Col xl={3} md={6}>
                          <Label className="form-label">Responsible</Label>
                          <Select
                              isMulti
                              isClearable
                              placeholder="All responsibles"
                              options={userOptions}
                              value={selectedResponsibles}
                              onChange={(value: any) => onResponsiblesChange(value || [])}
                              isDisabled={!userOptions.length}
                          />
                      </Col>
                      <Col xs={12} className="d-flex justify-content-end">
                          <Button
                              color="light"
                              outline
                              onClick={onReset}
                              disabled={!hasFilters}
                          >
                              Reset filters
                          </Button>
                      </Col>
                  </Row>
              </CardBody>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default FiltersPanel;
